import React, { useEffect, useState, useContext } from 'react';
import DeckGL from '@deck.gl/react';
import { GeoJsonLayer } from '@deck.gl/layers';
import { DataContext } from '../data/DataContext';
import { LightingEffect, AmbientLight, DirectionalLight, PointLight } from '@deck.gl/core';

const IndiaMap = () => {
  const formatIndianNumber = (num) => num.toLocaleString('en-IN');

  const ambientLight = new AmbientLight({ color: [255, 255, 255], intensity: 1.5 });
  const directionalLight = new DirectionalLight({
    color: [255, 255, 255],
    intensity: 1.9,
    direction: [-3, -9, -5]
  });
  const spotlight = new PointLight({
    color: [255, 255, 200],
    intensity: 3.5,
    position: [95.9629, 20.5937, 1000]
  });
  const lightingEffect = new LightingEffect({ ambientLight, directionalLight, spotlight });

  const [hoverData, setHoverData] = useState([]);
  const [geoData, setGeoData] = useState(null);
  const [districtToState, setDistrictToState] = useState({});
  const [stateScreenings, setStateScreenings] = useState({});
  const { data } = useContext(DataContext);

  // Load district-to-state mapping
  useEffect(() => {
    fetch("/districts.json")
      .then((res) => res.json())
      .then((districtsData) => {
        const mapping = {};
        ['v1', 'v2'].forEach(version => {
          if (districtsData[version]?.features) {
            districtsData[version].features.forEach(feature => {
              if (feature.properties?.name && feature.properties?.parent) {
                mapping[feature.properties.name.toLowerCase().trim()] =
                  feature.properties.parent.toLowerCase().trim();
              }
            });
          }
        });
        setDistrictToState(mapping);
      })
      .catch((error) => console.error('Districts fetch error:', error));
  }, []);

  // Aggregate screenings (Districts → States)
  useEffect(() => {
    if (hoverData.length > 0) {
      const screenings = {};

      hoverData.forEach(({ name, metric }) => {
        if (name && metric?.[0]?.count > 0) {
          const districtName = name.toLowerCase().trim();
          const parentState = districtToState[districtName];

          if (parentState) {
            screenings[parentState] = (screenings[parentState] || 0) + metric[0].count * 10;
          } else {
            const stateName = districtName;
            screenings[stateName] = (screenings[stateName] || 0) + metric[0].count * 10;
          }
        }
      });

      setStateScreenings(screenings);
    }
  }, [hoverData, districtToState]);

  useEffect(() => {
    if (data) {
      setHoverData(data.data?.hoverDataList || []);
    }
  }, [data]);

  useEffect(() => {
    fetch("/india_mini.json")
      .then((res) => res.json())
      .then(setGeoData)
      .catch((error) => console.error('GeoJSON fetch error:', error));
  }, []);

  const [hoveredState, setHoveredState] = useState(null);

  const handleHover = ({ object }) => {
    setHoveredState(object?.properties.name || null);
  };

  const hoverDataMap = hoverData.reduce((acc, item) => {
    acc[item.name?.toLowerCase()] = item;
    return acc;
  }, {});

  const layers =
    geoData &&
    new GeoJsonLayer({
      id: 'india-geojson',
      data: geoData,
      opacity: 0.8,
      stroked: false,
      filled: true,
      extruded: true,
      wireframe: true,
      getElevation: (f) => {
        const stateName = f.properties.name.toLowerCase();
        return (hoverDataMap[stateName]?.metric[0].count * 50) || (stateScreenings[stateName] * 5) || 0;
      },
      getFillColor: (f) => {
        if (f.properties.name === hoveredState) return [255, 193, 7]; // Highlight on hover
        return [93, 63, 153]; // Default purple
      },
      getLineColor: [255, 255, 255],
      pickable: true,
      onHover: handleHover,
      updateTriggers: {
        getFillColor: [hoveredState],
        getElevation: [hoverDataMap, stateScreenings]
      }
    });

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh" }}>
      <DeckGL
        initialViewState={{
          longitude: 79.9629,
          latitude: 18.5937,
          zoom: 4.3,
          pitch: 45,
          bearing: 30,
          maxPitch: 85,
        }}
        controller={true}
        layers={[layers]}
        effects={[lightingEffect]}
      />
      {hoveredState && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            width: '200px',
            backgroundColor: '#5d3f99',
            color: '#fff',
            padding: '1rem',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <h2 style={{ marginTop: 0, textTransform: "capitalize" }}>{hoveredState}</h2>
          <div style={{ backgroundColor: '#29295A', padding: '0.6rem', borderRadius: '4px' }}>
            <h2 style={{ fontSize: '1rem', color: "#ffc107" }}>
              {formatIndianNumber(stateScreenings[hoveredState.toLowerCase()] || 0)} Image(s)
            </h2>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndiaMap;
